import { useState, useEffect } from 'react';
import { fetchCompleteCourseData } from '../services/courseService';

/**
 * Custom hook for fetching course data
 * Handles loading, error, and data states
 *
 * @param {string} courseId - Course ID to fetch
 * @returns {Object} { courseData, loading, error, isValid }
 */
export const useCourseData = (courseId) => {
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (!courseId) {
      setError('Course ID is required');
      setLoading(false);
      setIsValid(false);
      return;
    }

    const loadCourse = async () => {
      setLoading(true);
      setError(null);
      setIsValid(true);

      try {
        const data = await fetchCompleteCourseData(courseId);

        // Validate required sections
        if (!data.metadata?.status === 'active') {
          console.warn('Course is not active');
        }

        if (!data.curriculum?.timeline?.length) {
          console.warn('Course has no curriculum');
        }

        if (!data.pricing) {
          console.warn('Course has no pricing');
        }

        setCourseData(data);
      } catch (err) {
        setError(err.message || 'Failed to load course data');
        setIsValid(false);
        console.error('Error loading course:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId]);

  return {
    courseData,
    loading,
    error,
    isValid
  };
};
